/* ═══════════════════════════════════════════════════════
   CrewCardBadge — "no card" / "card changed" / "card sent" on dashboard rows
   ═══════════════════════════════════════════════════════
   Shown only for jobs with a session today or tomorrow, so an admin sees a
   missing or stale crew card without opening the job. Reads the same rules
   the Docs & Files section uses (docs/crew-cards/DESIGN.md).
*/
import React from "react";
import { C, mono } from "../../../../lib/styles.js";
import { useDataOptional } from "../../../../context/DataContext.jsx";
import { crewCardId, sessionForCard, blankCrewCard, crewCardState, isSheetSessionEligible } from "../../../../lib/crewCardRules.js";

const addDays = (iso, n) => {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d + n);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function CrewCardBadge({ job, todayStr: todayProp }) {
  const data = useDataOptional();
  const todayStr = todayProp || localToday();
  if (!data || !job) return null;
  const { crewCards, contacts, studioSettings } = data;
  if (job.status !== "scheduled" && job.status !== "in_progress") return null;
  const tomorrow = addDays(todayStr, 1);
  const sessions = Array.isArray(job.sessions) && job.sessions.length
    ? job.sessions
    : (job.captureDate ? [sessionForCard(job, "root")] : []);
  // A flight day gets no call sheet (SHEET_SESSION_TYPES), so it never reads "no call sheet" here.
  const soon = sessions.filter(s => (s?.date === todayStr || s?.date === tomorrow) && isSheetSessionEligible("crew", s));
  if (soon.length === 0) return null;

  // `roster` goes into the snapshot: the server hashes the crew's phones from
  // it, and a badge built without them would read "changed" forever.
  const roster = studioSettings?.crewRoster || [];
  const states = soon.map(s => {
    const sid = s.id || "root";
    const card = (crewCards || []).find(c => c.id === crewCardId(job.id, sid)) || blankCrewCard(job.id, sid);
    return crewCardState({ job, session: s, card, contacts, roster }).state;
  });
  const state = states.includes("none") ? "none" : states.includes("changed") ? "changed" : "sent";
  const label = state === "none" ? "no call sheet" : state === "changed" ? "call sheet changed" : "call sheet sent";
  const color = state === "none" ? C.amber : state === "changed" ? C.purple : C.green;
  return (
    <span title={label} style={{ fontFamily: mono, fontSize: 9, letterSpacing: "0.06em", textTransform: "uppercase", padding: "2px 6px", borderRadius: 999, border: `1px solid ${color}66`, background: `${color}14`, color, marginLeft: 8, whiteSpace: "nowrap" }}>
      {label}
    </span>
  );
}
